/**
 * audioMuter.ts
 *
 * Mutes all audio sources while recording voice input to prevent
 * playback bleeding into the microphone capture.
 *
 * Tracks pre-mute state so playback can be restored after recording.
 */

import { stopAll as stopTtsPlayback } from './audioPlayback';
import { stopSound } from '../utils/alarmSounds';
import { browserPlaying, browserMuted, applyVolume } from '../composables/useBrowserPlayer';

let preMuteState: {
  browserWasPlaying: boolean;
  browserWasMuted: boolean;
} | null = null;

/**
 * Mute / stop all audio sources.
 * Safe to call multiple times — only the first call captures state.
 */
export function muteAllForRecording(): void {
  if (preMuteState) return; // already muted

  // Capture current state before muting
  preMuteState = {
    browserWasPlaying: browserPlaying.value,
    browserWasMuted: browserMuted.value,
  };

  // 1. Stop TTS / announcement playback (Web Audio API)
  try {
    stopTtsPlayback();
  } catch {
    /* ignore */
  }

  // 2. Stop alarm / timer sounds (Web Audio API oscillators)
  try {
    stopSound();
  } catch {
    /* ignore */
  }

  // 3. Cancel any browser speechSynthesis
  try {
    if (typeof window !== 'undefined' && window.speechSynthesis) {
      window.speechSynthesis.cancel();
    }
  } catch {
    /* ignore */
  }

  // 4. Mute the browser media player (HTML audio/video)
  if (browserPlaying.value) {
    browserMuted.value = true;
    applyVolume();
  }

  console.log('[AudioMuter] All audio muted for recording');
}

/**
 * Restore audio state from before recording.
 * Safe to call multiple times or when not muted.
 */
export function unmuteAfterRecording(): void {
  if (!preMuteState) return; // nothing to restore

  // Restore browser media player mute state
  if (preMuteState.browserWasPlaying && !preMuteState.browserWasMuted) {
    browserMuted.value = false;
    applyVolume();
  }

  preMuteState = null;
  console.log('[AudioMuter] Audio restored after recording');
}
